import {
  ConnectionListResult,
  FeatureFlagsContext,
  GridSortProps,
  MaxCountsResultData,
  PaginatedQueryWrapper,
} from '@tableau/data-catalog-shared';
import * as React from 'react';

import { Messages } from '../../../gen-src/Localize';
import { TablesDisplayGridRowDataFragment } from '../../../gen-src/queries/TablesDisplayGridRowDataFragment';
import {
  VirtualConnectionUpstreamTablesFilteredByTableQuery,
  VirtualConnectionUpstreamTablesFilteredByTableQuery_virtualConnections_tablesConnection_nodes_upstreamTablesConnection_nodes,
  VirtualConnectionUpstreamTablesFilteredByTableQueryVariables,
} from '../../../gen-src/queries/VirtualConnectionUpstreamTablesFilteredByTableQuery';
import {
  VirtualConnectionUpstreamTablesQuery,
  VirtualConnectionUpstreamTablesQuery_virtualConnections_upstreamTablesConnection_nodes,
  VirtualConnectionUpstreamTablesQueryVariables,
} from '../../../gen-src/queries/VirtualConnectionUpstreamTablesQuery';
import { DatabaseTableSortOrder } from '../../../gen-src/queries/globalTypes';
import { VirtualConnectionUpstreamTablesFilteredByTableQueryGql } from '../../queries/VirtualConnectionUpstreamTablesFilteredByTableQueryGql';
import { VirtualConnectionUpstreamTablesQueryGql } from '../../queries/VirtualConnectionUpstreamTablesQueryGql';
import { TablesDisplayGrid } from '../sharedDisplayGrid/TablesDisplayGrid';
import { TablesValidSortFields } from '../sorting/sortProviders/TablesGridSortProvider';
import { getRowDataWithMaxCountsForTable } from '../table/functions/TableRowDataFunctions';
import { TablesDisplayGridRowData } from '../table/types/TablesDisplayGridRowData';


type QueryVariables = VirtualConnectionUpstreamTablesQueryVariables;
type QueryResults = VirtualConnectionUpstreamTablesQuery;
type QueryResultListNode = VirtualConnectionUpstreamTablesQuery_virtualConnections_upstreamTablesConnection_nodes;
type GridRowData = TablesDisplayGridRowData;

type QueryVariablesWithFilter = VirtualConnectionUpstreamTablesFilteredByTableQueryVariables;
type QueryResultsWithFilter = VirtualConnectionUpstreamTablesFilteredByTableQuery;
type QueryResultListNodeWithFilter =
  VirtualConnectionUpstreamTablesFilteredByTableQuery_virtualConnections_tablesConnection_nodes_upstreamTablesConnection_nodes;

function selectConnectionListResult(resultData: QueryResults): ConnectionListResult<QueryResultListNode> | null {
  return resultData && resultData.virtualConnections && resultData.virtualConnections[0] &&
    resultData.virtualConnections[0].upstreamTablesConnection;
}

function selectConnectionListResultForFilteredQuery(
  resultData: QueryResultsWithFilter
): ConnectionListResult<QueryResultListNodeWithFilter> | null {
  const virtualConnection = resultData && resultData.virtualConnections && resultData.virtualConnections[0];
  const virtualConnectionTables = virtualConnection && virtualConnection.tablesConnection && virtualConnection.tablesConnection.nodes;
  const table = virtualConnectionTables && virtualConnectionTables[0];
  return table ? table.upstreamTablesConnection : null;
}

function selectMaxCountsResult(resultData: QueryResults): MaxCountsResultData {
  return resultData && resultData.virtualConnections && resultData.virtualConnections[0];
}

function selectMaxCountsResultForFilteredQuery(resultData: QueryResultsWithFilter): MaxCountsResultData | null {
  const virtualConnection = resultData && resultData.virtualConnections && resultData.virtualConnections[0];
  const virtualConnectionTables = virtualConnection && virtualConnection.tablesConnection && virtualConnection.tablesConnection.nodes;
  const table = virtualConnectionTables && virtualConnectionTables[0];
  return table;
}

function getRowDataForTable(
  table: TablesDisplayGridRowDataFragment,
  resultData: QueryResults,
): TablesDisplayGridRowData {
  return getRowDataWithMaxCountsForTable(table, selectMaxCountsResult(resultData));
}

function getRowDataForTableForFilteredQuery(
  table: TablesDisplayGridRowDataFragment,
  resultData: QueryResultsWithFilter,
): TablesDisplayGridRowData {
  return getRowDataWithMaxCountsForTable(table, selectMaxCountsResultForFilteredQuery(resultData));
}

export type VirtualConnectionUpstreamTablesQueryGridProps = GridSortProps<TablesValidSortFields> & {
  readonly vizportalUrlId: string;
  readonly filteredTableId: string | null;
  readonly rowCount: number | null;
};

export function VirtualConnectionUpstreamTablesQueryGrid(props: VirtualConnectionUpstreamTablesQueryGridProps): JSX.Element {
  const { publishedConnectionsLineageEnabled } = React.useContext(FeatureFlagsContext);

  const emptyGridMessage = Messages.EmptyUpstreamTablesLineageGridMessage();
  const emptyFilteredGridMessage = Messages.EmptyUpstreamFilteredTablesLineageGridMessage();

  return props.filteredTableId ? (
    // Query with Selected Table as Filter
    <PaginatedQueryWrapper<QueryVariablesWithFilter, QueryResultsWithFilter, QueryResultListNode, GridRowData>
      queryGql={VirtualConnectionUpstreamTablesFilteredByTableQueryGql}
      partialQueryVariables={{
        vizportalUrlId: props.vizportalUrlId,
        selectedTableId: props.filteredTableId,
        orderBy: props.orderBy as DatabaseTableSortOrder,
        publishedConnectionsLineageEnabled
      }}
      selectConnectionListResult={selectConnectionListResultForFilteredQuery}
      mapResultNode={getRowDataForTableForFilteredQuery}
    >
      {({ rowStore }) =>
        <TablesDisplayGrid
          rowStore={rowStore}
          emptyGridMessage={emptyFilteredGridMessage}
          rowCount={props.rowCount}
          orderBy={props.orderBy}
          updateSortOrder={props.updateSortOrder}
        />
      }
    </PaginatedQueryWrapper>
  ) : (
    // Query without Selection
    <PaginatedQueryWrapper<QueryVariables, QueryResults, QueryResultListNode, GridRowData>
      queryGql={VirtualConnectionUpstreamTablesQueryGql}
      partialQueryVariables={{
        vizportalUrlId: props.vizportalUrlId,
        orderBy: props.orderBy as DatabaseTableSortOrder,
        publishedConnectionsLineageEnabled
      }}
      selectConnectionListResult={selectConnectionListResult}
      mapResultNode={getRowDataForTable}
    >
      {({ rowStore }) =>
        <TablesDisplayGrid
          rowStore={rowStore}
          emptyGridMessage={emptyGridMessage}
          rowCount={props.rowCount}
          orderBy={props.orderBy}
          updateSortOrder={props.updateSortOrder}
        />}
    </PaginatedQueryWrapper>
  );
}
